"use client";

import { Icon, Image, Link, Text, View } from "@/components";
import { useLayout } from "@/providers";
import type { CardProjectProps } from "@/types";
import type { JSX } from "react";

/**
 * CardProject
 *
 * @description
 * Organism-level component that renders a project card.
 * Displays project title, subtitle, associated users, and pending tasks count.
 * Matches the specific design requirements with a large icon, user stack, and status pill.
 *
 * @component
 *
 * @param {CardProjectProps} props - Component properties.
 * @param {string} props.id - Project ID.
 * @param {string} props.title - Project title (e.g., "Decreto 779").
 * @param {string} props.subtitle - Project description/subtitle (e.g., "Sistema web").
 * @param {string[]} props.users - Array of user avatar URLs.
 * @param {number} props.pendingTasks - Number of pending tasks.
 *
 * @returns {JSX.Element} A rendered project card component.
 */
const CardProject = ({
  id,
  title,
  subtitle,
  users,
  pendingTasks
}: CardProjectProps): JSX.Element => {
  const { t } = useLayout();

  return (
    <Link
      replace
      href={`/backoffice/projects/${id}`}
      className="block bg-card rounded-[2rem] p-6 shadow-sm border border-transparent dark:border-border w-full hover:shadow-md transition-shadow duration-300"
    >
      {/* Header Section */}
      <View className="flex items-center gap-5 mb-8">
        <View className="w-[84px] h-[84px] rounded-full bg-primary shrink-0" />
        <View className="flex flex-col">
          <Text
            type="h3"
            className="text-primary dark:text-foreground font-extrabold text-2xl leading-tight"
          >
            {title}
          </Text>
          <Text
            type="p"
            className="text-primary dark:text-muted-foreground text-lg font-normal"
          >
            {subtitle}
          </Text>
        </View>
      </View>

      {/* Separator */}
      <View className="h-px bg-border w-full mb-3" />

      {/* Content Section */}
      <View className="flex flex-col gap-2">
        {/* Technology Icon */}
        <View className="flex justify-end pr-1">
          <View className="text-primary dark:text-foreground">
            {/* Hexagon C++ Icon Simulation */}
            <Icon name="laptop-code" size="4x" />
          </View>
        </View>

        {/* Status Pill */}
        <View className="bg-muted dark:bg-card-foreground/5 rounded-full py-2 px-2 sm:px-3 flex items-center justify-between min-h-[48px] gap-2 overflow-hidden">
          <View className="flex -space-x-3 sm:-space-x-2.5 pl-1 flex-shrink-0">
            {users.slice(0, 4).map((userImage, index: number) => (
              <View
                key={userImage}
                className="relative w-7 h-7 sm:w-8 sm:h-8 rounded-full border-[1.5px] border-card overflow-hidden"
              >
                <Image
                  src={userImage}
                  alt={`User ${index + 1}`}
                  width={40}
                  height={40}
                  className="object-cover"
                />
              </View>
            ))}
            {users.length > 4 && (
              <View className="relative w-7 h-7 sm:w-8 sm:h-8 rounded-full border-[1.5px] border-card bg-muted flex items-center justify-center font-bold text-muted-foreground">
                <Text className="text-[10px] sm:text-xs">
                  +{users.length - 4}
                </Text>
              </View>
            )}
          </View>
          <Text className="text-muted-foreground text-[10px] sm:text-sm font-medium truncate min-w-0">
            {pendingTasks} {t("pendingTasks")}
          </Text>
        </View>
      </View>
    </Link>
  );
};

export default CardProject;
